(function (root, factory) {
  'use strict';
  const api = factory();
  if (typeof module === 'object' && module.exports) module.exports = api;
  root.MHMCreditProgress = api;
})(typeof globalThis !== 'undefined' ? globalThis : this, function () {
  'use strict';

  const DEFAULT_LADDER = '150:10,300:20,450:30,600:40,800:50';
  // Match Python str.strip / re \s, including NEL and information separators,
  // but excluding U+FEFF (which JavaScript's native trim / \s would remove).
  const PYTHON_SPACE = '[\\u0009-\\u000d\\u001c-\\u0020\\u0085\\u00a0\\u1680\\u2000-\\u200a\\u2028\\u2029\\u202f\\u205f\\u3000]';
  const STRIP = new RegExp('^' + PYTHON_SPACE + '+|' + PYTHON_SPACE + '+$', 'g');
  const CLUB = new RegExp('buyers' + PYTHON_SPACE + '*club|55%' + PYTHON_SPACE + '*(?:of|off)' + PYTHON_SPACE + '*retail', 'i');

  function pythonStrip(value) {
    return typeof value === 'string' ? value.replace(STRIP, '') : '';
  }

  function clubDiscount(application) {
    const raw = application && (application.title || application.code);
    if (typeof raw !== 'string') return false;
    // Python re.I also matches these Unicode equivalents of ASCII letters.
    const title = raw.replace(/[\u0130\u0131]/g, 'i').replace(/\u017f/g, 's').replace(/\u212a/g, 'k');
    return CLUB.test(title);
  }

  function dollarsToCents(value) {
    const text = pythonStrip(value);
    if (!/^(?:\d+(?:\.\d{1,2})?|\.\d{1,2})$/.test(text)) return null;
    const parts = text.split('.');
    const whole = Number(parts[0] || '0');
    const fractional = Number(((parts[1] || '') + '00').slice(0, 2));
    const cents = whole * 100 + fractional;
    return Number.isSafeInteger(cents) && cents > 0 ? cents : null;
  }

  function parseLadder(text = DEFAULT_LADDER) {
    if (typeof text !== 'string' || !pythonStrip(text)) return null;
    const rungs = [];
    for (const pair of text.split(',')) {
      const values = pair.split(':');
      if (values.length !== 2) return null;
      const threshold = dollarsToCents(values[0]);
      const credit = dollarsToCents(values[1]);
      if (threshold === null || credit === null) return null;
      rungs.push({ threshold_cents: threshold, credit_cents: credit });
    }
    rungs.sort((a, b) => a.threshold_cents - b.threshold_cents);
    for (let index = 1; index < rungs.length; index += 1) {
      if (rungs[index].threshold_cents === rungs[index - 1].threshold_cents ||
          rungs[index].credit_cents < rungs[index - 1].credit_cents) return null;
    }
    return rungs;
  }

  function validCents(value) {
    return Number.isSafeInteger(value) && value >= 0;
  }

  function excluded(line) {
    const product = line.product;
    if (line.gift_card || !product || Object.keys(product).length === 0) return true;
    if (pythonStrip(line.vendor).toLowerCase() === 'myhairmail') return true;
    if (product.type === 'Care Products' || product.type === 'Hair Care') return true;
    if (Array.isArray(product.collections) && product.collections.includes('wig-styling-tools')) return true;
    return line.discount_allocations.some((allocation) => clubDiscount(allocation.discount_application));
  }

  function compute(input = {}) {
    const result = {
      visible: false,
      reason: 'disabled',
      eligible_cents: 0,
      earned_cents: 0,
      next_threshold_cents: null,
      next_credit_cents: null,
      remaining_cents: 0,
      rungs: [],
      max_threshold_cents: 0,
      progress_percent: 0,
      current_rung: -1
    };
    if (!input || input.enabled !== true) return result;
    if (input.currency !== 'USD') return { ...result, reason: 'currency' };
    const rungs = parseLadder(input.ladder);
    if (!rungs) return { ...result, reason: 'invalid_ladder' };
    result.rungs = rungs;
    result.max_threshold_cents = rungs[rungs.length - 1].threshold_cents;
    if (!Array.isArray(input.lines)) return { ...result, reason: 'invalid_cart' };

    let eligible = 0;
    let total = 0;
    for (const line of input.lines) {
      if (!line || !validCents(line.original_line_price) || !Array.isArray(line.discount_allocations)) {
        return { ...result, reason: 'invalid_cart' };
      }
      let discounts = 0;
      for (const allocation of line.discount_allocations) {
        if (!allocation || !validCents(allocation.amount) || !Number.isSafeInteger(discounts + allocation.amount)) {
          return { ...result, reason: 'invalid_cart' };
        }
        discounts += allocation.amount;
      }
      // Prices are already line totals; multiplying by quantity would double count.
      const net = Math.max(line.original_line_price - discounts, 0);
      total += net;
      if (!excluded(line)) eligible += net;
      if (!Number.isSafeInteger(total) || !Number.isSafeInteger(eligible)) {
        return { ...result, reason: 'invalid_cart' };
      }
    }

    if (Object.prototype.hasOwnProperty.call(input, 'cart_total_price')) {
      if (!validCents(input.cart_total_price)) return { ...result, reason: 'invalid_cart' };
      if (total !== input.cart_total_price) return { ...result, reason: 'allocation_mismatch' };
    }
    result.eligible_cents = eligible;
    if (eligible <= 0) return { ...result, reason: 'no_eligible_items' };

    result.visible = true;
    result.progress_percent = Math.min(100, eligible / result.max_threshold_cents * 100);
    for (let index = 0; index < rungs.length; index += 1) {
      if (eligible >= rungs[index].threshold_cents) {
        result.current_rung = index;
        result.earned_cents = rungs[index].credit_cents;
      } else {
        result.next_threshold_cents = rungs[index].threshold_cents;
        result.next_credit_cents = rungs[index].credit_cents;
        result.remaining_cents = rungs[index].threshold_cents - eligible;
        break;
      }
    }
    result.reason = result.current_rung < 0 ? 'below_first_rung' :
      (result.next_threshold_cents === null ? 'maximum' : 'earned');
    return result;
  }

  return { compute, parseLadder };
});

// Symmetry replaces the subtotal/footer with section HTML on every cart change.
// Each replacement reconnects this element; no polling, cart writes, or event shim.
(function () {
  'use strict';
  if (typeof window === 'undefined' || !window.customElements ||
      window.customElements.get('mhm-credit-progress')) return;

  // Keep one empty live region outside the section HTML that Symmetry replaces.
  // A populated live region inserted from scratch is not reliably announced.
  const announcer = document.createElement('p');
  announcer.className = 'mhm-credit-progress__announcement';
  announcer.setAttribute('aria-live', 'polite');
  announcer.setAttribute('aria-atomic', 'true');
  document.body.append(announcer);
  let lastAnnouncement = '';
  let announcementTimer;

  class MHMCreditProgressElement extends HTMLElement {
    connectedCallback() {
      // Let the JSON and the initially empty live region finish connecting first.
      queueMicrotask(() => { if (this.isConnected) this.render(); });
    }

    render() {
      this.hidden = true;
      try {
        const input = JSON.parse(this.querySelector('[data-credit-cart]').textContent);
        const state = window.MHMCreditProgress.compute(input);
        this.dataset.creditState = state.reason;
        if (!state.visible) return;
        const formatter = window.theme && window.theme.Shopify && window.theme.Shopify.formatMoney;
        if (typeof formatter !== 'function') return;
        const money = (cents, compact = false) => {
          const template = document.createElement('template');
          template.innerHTML = formatter(cents, input.money_format);
          const text = template.content.textContent;
          return compact && cents % 100 === 0 ? text.replace(/([.,])00(?=\D*$)/, '') : text;
        };
        const message = this.querySelector('[data-credit-message]');
        message.replaceChildren();
        const append = (text) => message.append(document.createTextNode(text));
        const amount = (cents, compact = false) => {
          const strong = document.createElement('strong');
          strong.textContent = money(cents, compact);
          message.append(strong);
        };
        if (state.earned_cents === 0) {
          append('Add '); amount(state.remaining_cents);
          append(' more to earn '); amount(state.next_credit_cents, true);
          append(' in Bonus Credit for your next order.');
        } else if (state.next_threshold_cents !== null) {
          append("You've earned "); amount(state.earned_cents, true);
          append(' in Bonus Credit! Add '); amount(state.remaining_cents);
          append(' more to get '); amount(state.next_credit_cents, true); append('.');
        } else {
          append("You've earned the maximum "); amount(state.earned_cents, true);
          append(' in Bonus Credit!');
        }
        this.querySelector('[data-credit-fill]').style.width = state.progress_percent + '%';
        const rungs = this.querySelector('[data-credit-rungs]');
        rungs.replaceChildren();
        state.rungs.forEach((rung, index) => {
          const tick = document.createElement('li');
          tick.style.left = (rung.threshold_cents / state.max_threshold_cents * 100) + '%';
          tick.classList.toggle('is-reached', index <= state.current_rung);
          tick.classList.toggle('is-current', index === state.current_rung);
          tick.title = money(rung.credit_cents, true) + ' Bonus Credit at ' + money(rung.threshold_cents, true);
          const label = document.createElement('span');
          label.textContent = money(rung.threshold_cents, true);
          tick.append(label);
          rungs.append(tick);
        });
        this.dataset.eligibleCents = state.eligible_cents;
        this.dataset.earnedCents = state.earned_cents;
        this.hidden = false;
        if (message.textContent !== lastAnnouncement) {
          lastAnnouncement = message.textContent;
          clearTimeout(announcementTimer);
          announcementTimer = setTimeout(() => {
            announcer.textContent = lastAnnouncement;
          }, 100);
        }
      } catch (_) {
        this.dataset.creditState = 'render_error';
      }
    }
  }
  window.customElements.define('mhm-credit-progress', MHMCreditProgressElement);
})();
